class ArtsPanelController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
  }

  getArtCategories() {
    const categoriesByKey = new Map();
    this.orchestrator.ARTS.forEach(art => {
      if (art.category?.key) {
        categoriesByKey.set(art.category.key, art.category.name);
      }
    });
    return [...categoriesByKey.entries()].map(([key, name]) => ({ key, name }));
  }

  getArtCategoriesList() {
    return [{ key: 'todas', name: 'Todas' }, ...this.getArtCategories()];
  }

  getActiveCategoryName() {
    const { activeCategoryKey } = this.orchestrator.selectionState;
    const category = this.getArtCategoriesList().find(
      item => item.key === activeCategoryKey,
    );
    return category?.name || 'Todas';
  }

  getFilteredArts() {
    const { ARTS, selectionState } = this.orchestrator;
    if (selectionState.activeCategoryKey === 'todas') return ARTS;
    return ARTS.filter(
      art => art.category?.key === selectionState.activeCategoryKey,
    );
  }

  isArtAllowedForColor(
    art,
    colorKey = this.orchestrator.selectionState.selectedColorKey,
  ) {
    const blocked = art?.colorsNotAllowed;
    if (!Array.isArray(blocked) || blocked.length === 0) return true;
    return !blocked.includes(colorKey);
  }

  syncUiAfterArtChange() {
    const {
      textCustomizationController,
      stepNavigationController,
      finishCheckoutController,
      bottleSceneController,
    } = this.orchestrator;
    const art = this.orchestrator.selectionState.getSelectedArt();

    textCustomizationController.clearTextIfArtIncompatible(art);
    textCustomizationController.syncTextLayoutOptions();
    stepNavigationController.syncStepNavAvailability();
    stepNavigationController.syncNextStepButton();

    if (stepNavigationController.getActiveStepKey() === 'finish') {
      finishCheckoutController.syncFinishStep();
    }
    if (
      !stepNavigationController.canNavigateToTexts() &&
      stepNavigationController.getActiveStepKey() === 'texts'
    ) {
      stepNavigationController.setActiveStep('arts');
    }
    if (
      !stepNavigationController.canNavigateToFinish() &&
      stepNavigationController.getActiveStepKey() === 'finish'
    ) {
      stepNavigationController.setActiveStep('arts');
    }

    bottleSceneController.applySelection();
    this.syncRemoveArtButton();
  }

  clearSelectedArtIfDisallowed() {
    const { selectionState } = this.orchestrator;
    const art = selectionState.getSelectedArt();
    if (!art || this.isArtAllowedForColor(art)) return false;

    selectionState.selectedArtKey = null;
    this.syncUiAfterArtChange();
    return true;
  }

  clearSelectedArt() {
    const { selectionState } = this.orchestrator;
    if (!selectionState.selectedArtKey) return false;

    selectionState.selectedArtKey = null;
    this.renderArtsGrid();
    this.syncUiAfterArtChange();
    return true;
  }

  syncRemoveArtButton() {
    const { stepContent } = this.orchestrator.dom;
    const button = stepContent?.querySelector('.arts-remove-selection');
    if (!button) return;

    button.hidden = !this.orchestrator.selectionState.selectedArtKey;
  }

  closeArtsCategoryMenu() {
    const { stepContent } = this.orchestrator.dom;
    const menu = stepContent?.querySelector('.arts-category-menu');
    const trigger = stepContent?.querySelector('.arts-category-trigger');
    if (menu) menu.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  syncArtsGridFade() {
    const { selectionState } = this.orchestrator;
    const { stepContent } = this.orchestrator.dom;

    if (
      !selectionState.artsGridWrapElement ||
      !selectionState.artsGridElement
    ) {
      selectionState.artsGridWrapElement =
        stepContent?.querySelector('.arts-grid-wrap');
      selectionState.artsGridElement = stepContent?.querySelector('.arts-grid');
    }
    if (
      !selectionState.artsGridWrapElement ||
      !selectionState.artsGridElement
    ) {
      return;
    }

    const grid = selectionState.artsGridElement;
    const remaining = grid.scrollHeight - grid.scrollTop - grid.clientHeight;
    const hasMore = remaining > 8;
    if (selectionState.artsHasMoreCached === hasMore) return;
    selectionState.artsHasMoreCached = hasMore;
    selectionState.artsGridWrapElement.classList.toggle('has-more', hasMore);
  }

  scheduleArtsGridFade() {
    const { selectionState } = this.orchestrator;
    if (selectionState.artsFadeAnimationFrame) return;
    selectionState.artsFadeAnimationFrame = requestAnimationFrame(() => {
      selectionState.artsFadeAnimationFrame = 0;
      this.syncArtsGridFade();
    });
  }

  handleArtCardSelection(art, grid, button) {
    const { selectionState } = this.orchestrator;

    if (!this.isArtAllowedForColor(art)) return;

    selectionState.selectedArtKey = art.key;
    grid
      .querySelectorAll('.art-card')
      .forEach(card => card.classList.remove('active'));
    button.classList.add('active');
    this.syncUiAfterArtChange();
  }

  renderArtsGrid() {
    const { selectionState } = this.orchestrator;
    const { stepContent } = this.orchestrator.dom;
    const grid =
      selectionState.artsGridElement || stepContent.querySelector('.arts-grid');
    if (!grid) return;
    selectionState.artsGridElement = grid;

    const arts = this.getFilteredArts();
    const fragment = document.createDocumentFragment();
    const colorKey = selectionState.selectedColorKey;

    arts.forEach(art => {
      const isBlocked = !this.isArtAllowedForColor(art, colorKey);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `art-card art-card--${art.type || 'full'}`;
      button.dataset.art = art.key;
      button.title = isBlocked
        ? `${art.name} (indisponível nesta cor)`
        : art.name;

      if (isBlocked) {
        button.classList.add('disabled');
        button.disabled = true;
      }

      if (!isBlocked && selectionState.selectedArtKey === art.key) {
        button.classList.add('active');
      }

      button.innerHTML = `
      <span class="art-card__media">
        <img
          src="${art.previewUrl || art.url}"
          alt="${art.name}"
          loading="lazy"
          decoding="async"
          crossorigin="anonymous"
        />
      </span>
    `;

      button.addEventListener('click', () => {
        this.handleArtCardSelection(art, grid, button);
      });

      fragment.appendChild(button);
    });

    grid.replaceChildren(fragment);
    selectionState.artsHasMoreCached = null;
    this.scheduleArtsGridFade();
  }

  buildArtsPanel() {
    const { selectionState } = this.orchestrator;
    const { stepContent } = this.orchestrator.dom;
    if (!stepContent || stepContent.dataset.ready === 'true') return;
    stepContent.dataset.ready = 'true';

    const header = document.createElement('div');
    header.className = 'arts-header';

    const headerLeft = document.createElement('div');
    headerLeft.className = 'arts-header-left';

    const title = document.createElement('h3');
    title.className = 'arts-title';
    title.textContent = 'Estampas';

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'arts-remove-selection';
    removeButton.textContent = 'Remover';
    removeButton.hidden = !selectionState.selectedArtKey;
    removeButton.addEventListener('click', () => {
      this.clearSelectedArt();
    });

    headerLeft.appendChild(title);
    headerLeft.appendChild(removeButton);

    const filter = document.createElement('div');
    filter.className = 'arts-category-filter';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'arts-category-trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = `
    <span class="arts-category-trigger-icon" aria-hidden="true">
      <span></span><span></span><span></span>
    </span>
    <span class="arts-category-trigger-label">${this.getActiveCategoryName()}</span>
    <span class="arts-category-trigger-chevron" aria-hidden="true"></span>
  `;

    const menu = document.createElement('div');
    menu.className = 'arts-category-menu';
    menu.hidden = true;
    menu.setAttribute('role', 'listbox');

    const categories = this.getArtCategoriesList();
    categories.forEach(category => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'arts-category-option';
      option.dataset.category = category.key;
      option.setAttribute('role', 'option');
      option.setAttribute(
        'aria-selected',
        category.key === selectionState.activeCategoryKey ? 'true' : 'false',
      );
      if (category.key === selectionState.activeCategoryKey) {
        option.classList.add('active');
      }

      option.innerHTML = `
      <span class="arts-category-radio" aria-hidden="true"></span>
      <span class="arts-category-option-label">${category.name}</span>
    `;

      option.addEventListener('click', () => {
        selectionState.activeCategoryKey = category.key;

        menu.querySelectorAll('.arts-category-option').forEach(item => {
          const isActive =
            item.dataset.category === selectionState.activeCategoryKey;
          item.classList.toggle('active', isActive);
          item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        const label = trigger.querySelector('.arts-category-trigger-label');
        if (label) label.textContent = category.name;

        this.closeArtsCategoryMenu();
        this.renderArtsGrid();
      });

      menu.appendChild(option);
    });

    trigger.addEventListener('click', event => {
      event.stopPropagation();
      const isOpen = !menu.hidden;
      menu.hidden = isOpen;
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });

    document.addEventListener('click', event => {
      if (!filter.contains(event.target)) this.closeArtsCategoryMenu();
    });

    filter.appendChild(trigger);
    filter.appendChild(menu);
    header.appendChild(headerLeft);
    header.appendChild(filter);

    const gridWrap = document.createElement('div');
    gridWrap.className = 'arts-grid-wrap';
    selectionState.artsGridWrapElement = gridWrap;

    const grid = document.createElement('div');
    grid.className = 'arts-grid';
    selectionState.artsGridElement = grid;
    grid.addEventListener('scroll', () => this.scheduleArtsGridFade(), {
      passive: true,
    });

    gridWrap.appendChild(grid);
    stepContent.appendChild(header);
    stepContent.appendChild(gridWrap);
    this.renderArtsGrid();
    window.addEventListener('resize', () => this.scheduleArtsGridFade());
  }
}

window.ArtsPanelController = ArtsPanelController;
